import {
	AlertCircle,
	AlertOctagon,
	ArrowDown,
	ArrowDownUp,
	ArrowUp,
	Ban,
	Bell,
	BellRing,
	Bold,
	BookOpenText,
	Bot,
	Braces,
	Check,
	CheckCheck,
	CheckCircle2,
	CheckSquare,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	ChevronUp,
	ChevronsDown,
	ChevronsUp,
	CircleDot,
	CircleHelp,
	Clock,
	Clock3,
	Code,
	CodeXml,
	Copy,
	CornerDownRight,
	Database,
	Edit3,
	Eraser,
	ExternalLink,
	Eye,
	EyeOff,
	File,
	FileArchive,
	FileAudio,
	FileCode,
	FileDiff,
	FileDown,
	FileEdit,
	FileImage,
	FileJson,
	FileKey,
	FileLock,
	FileSliders,
	FileSpreadsheet,
	FileTerminal,
	FileText,
	FileUp,
	FileVideo,
	Filter,
	Fish,
	Folder,
	FolderGit2,
	FolderOpen,
	GitBranch,
	GitMerge,
	GitPullRequest,
	GitPullRequestClosed,
	GitPullRequestDraft,
	Gitlab,
	Globe,
	GripVertical,
	Info,
	Italic,
	Link,
	Link2,
	List,
	Loader2,
	LocateFixed,
	Maximize2,
	MessageSquare,
	MessagesSquare,
	Minimize2,
	Moon,
	MoreVertical,
	Package,
	Palette,
	PanelLeftClose,
	PanelLeftOpen,
	Plus,
	Quote,
	RefreshCcw,
	Reply,
	RotateCcw,
	Save,
	Scale,
	Search,
	Send,
	Settings,
	ShieldCheck,
	SmilePlus,
	Sun,
	ThumbsDown,
	ThumbsUp,
	Train,
	Trash2,
	TriangleAlert,
	User,
	UserRound,
	WrapText,
	X,
	XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const ICON_CLASS = "h-4 w-4 text-[var(--color-text-secondary)]";
const ICONS = {
	AlertCircle,
	AlertOctagon,
	ArrowDown,
	ArrowDownUp,
	ArrowUp,
	Ban,
	Bell,
	BellRing,
	Bold,
	BookOpenText,
	Bot,
	Braces,
	Check,
	CheckCheck,
	CheckCircle2,
	CheckSquare,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	ChevronUp,
	ChevronsDown,
	ChevronsUp,
	CircleDot,
	CircleHelp,
	Clock,
	Clock3,
	Code,
	CodeXml,
	Copy,
	CornerDownRight,
	Database,
	Edit3,
	Eraser,
	ExternalLink,
	Eye,
	EyeOff,
	File,
	FileArchive,
	FileAudio,
	FileCode,
	FileDiff,
	FileDown,
	FileEdit,
	FileImage,
	FileJson,
	FileKey,
	FileLock,
	FileSliders,
	FileSpreadsheet,
	FileTerminal,
	FileText,
	FileUp,
	FileVideo,
	Filter,
	Fish,
	Folder,
	FolderGit2,
	FolderOpen,
	GitBranch,
	GitMerge,
	GitPullRequest,
	GitPullRequestClosed,
	GitPullRequestDraft,
	Gitlab,
	Globe,
	GripVertical,
	Info,
	Italic,
	Link,
	Link2,
	List,
	Loader2,
	LocateFixed,
	Maximize2,
	MessageSquare,
	MessagesSquare,
	Minimize2,
	Moon,
	MoreVertical,
	Package,
	Palette,
	PanelLeftClose,
	PanelLeftOpen,
	Plus,
	Quote,
	RefreshCcw,
	Reply,
	RotateCcw,
	Save,
	Scale,
	Search,
	Send,
	Settings,
	ShieldCheck,
	SmilePlus,
	Sun,
	ThumbsDown,
	ThumbsUp,
	Train,
	Trash2,
	TriangleAlert,
	User,
	UserRound,
	WrapText,
	X,
	XCircle,
};

const LEGEND_ITEMS = [
	{
		icon: "Gitlab",
		label: "GitLab profile",
		description: "Open your GitLab profile from the header.",
	},
	{
		icon: "GitPullRequest",
		label: "Main",
		description: "Primary merge request dashboard tab.",
	},
	{
		icon: "Eye",
		label: "Watching",
		description: "MRs you are watching or a watched MR marker.",
	},
	{
		icon: "FolderGit2",
		label: "Repository",
		description: "Repository or repo-grouped dashboard view.",
	},
	{
		icon: "Search",
		label: "Search",
		description: "Filter visible MRs, files, repos, or settings.",
	},
	{
		icon: "Filter",
		label: "Filters",
		description: "Open dashboard filters and view settings.",
	},
	{
		icon: "RefreshCcw",
		label: "Refresh",
		description: "Reload dashboard data or diff content.",
	},
	{
		icon: "Bell",
		label: "Notifications",
		description: "Watched MRs have unseen activity.",
	},
	{
		icon: "BellRing",
		label: "Unread activity",
		description: "New activity exists on watched MRs.",
	},
	{
		icon: "Sun",
		label: "Light theme",
		description: "Theme switcher light-mode option.",
	},
	{
		icon: "Moon",
		label: "Dark/stale",
		description: "Dark theme option or stale MR indicator.",
	},
	{
		icon: "Maximize2",
		label: "Expand",
		description: "Expand width, a modal, or a comment.",
	},
	{
		icon: "Minimize2",
		label: "Constrain",
		description: "Return dashboard to constrained width.",
	},
	{
		icon: "Settings",
		label: "Settings",
		description: "Open configurable dashboard options.",
	},
	{
		icon: "X",
		label: "Close/clear",
		description: "Close a panel/modal or clear a value.",
	},

	{
		custom: "dot-green",
		label: "Ready dot",
		description: "CI passed and approvals are complete.",
	},
	{
		custom: "dot-red",
		label: "Failing dot",
		description: "Pipeline has failing checks.",
	},
	{
		custom: "dot-yellow",
		label: "Review dot",
		description: "Awaiting review or has unresolved comments.",
	},
	{
		custom: "dot-gray",
		label: "Progress dot",
		description: "CI or review is still in progress.",
	},
	{
		icon: "MessageSquare",
		label: "Comments",
		description: "Open or view MR comments and discussions.",
	},
	{
		icon: "MessagesSquare",
		label: "Discussion",
		description: "Diff or thread discussion context.",
	},
	{
		icon: "FileDiff",
		label: "Diff",
		description: "Open the inline MR diff viewer.",
	},
	{ icon: "Link", label: "Copy link", description: "Copy or insert a link." },
	{
		icon: "Link2",
		label: "MR links",
		description: "Copy one or more MR links.",
	},
	{
		icon: "GitBranch",
		label: "Branch",
		description: "Branch, checkout command, or branch filter.",
	},
	{
		icon: "Bot",
		label: "Bot/AI",
		description: "Bot author or missing AI review status.",
	},
	{
		icon: "GitPullRequestDraft",
		label: "Draft",
		description: "Merge request is marked draft.",
	},
	{ icon: "UserRound", label: "Author", description: "MR author identity." },
	{
		icon: "User",
		label: "User",
		description: "Author filter, reviewer, or your MR marker.",
	},
	{
		icon: "Clock3",
		label: "Timestamp",
		description: "MR created or updated time.",
	},
	{
		icon: "Clock",
		label: "Waiting/running",
		description: "Waiting for review or a running pipeline.",
	},
	{
		icon: "Scale",
		label: "Large MR",
		description: "MR changes a large number of lines.",
	},
	{
		icon: "Eraser",
		label: "Cleanup MR",
		description: "MR mostly removes or replaces code.",
	},
	{
		icon: "Info",
		label: "Info",
		description: "Missing size data or informational rule.",
	},
	{
		icon: "Ban",
		label: "Blocked",
		description: "Merge conflict or exclude-like rule.",
	},
	{
		icon: "TriangleAlert",
		label: "Rule warning",
		description: "Project rules may block review or merge.",
	},
	{
		icon: "CheckSquare",
		label: "Blocking thread",
		description: "Unresolved blocking discussions.",
	},
	{
		icon: "AlertCircle",
		label: "Attention",
		description: "Missing reviewer, failed repo stat, or warning.",
	},
	{
		icon: "AlertOctagon",
		label: "Needs review",
		description: "MR needs your review.",
	},

	{
		icon: "CircleDot",
		label: "No pipeline",
		description: "No CI pipeline is available yet.",
	},
	{
		icon: "CheckCircle2",
		label: "Passed",
		description: "Pipeline or validation succeeded.",
	},
	{
		icon: "XCircle",
		label: "Failed",
		description: "Pipeline or validation failed.",
	},
	{
		icon: "Train",
		label: "Merge train",
		description: "Pipeline belongs to a merge train.",
	},
	{
		icon: "GitMerge",
		label: "Merged/ready",
		description: "MR is merged or ready to merge.",
	},
	{
		icon: "GitPullRequestClosed",
		label: "Closed",
		description: "Merge request was closed without merge.",
	},
	{
		icon: "Check",
		label: "Selected/success",
		description: "Selection, copy success, or completed action.",
	},
	{
		icon: "CheckCheck",
		label: "Mark all seen",
		description: "Clear all notification activity.",
	},

	{
		icon: "ExternalLink",
		label: "Open external",
		description: "Open GitLab, a repo, comment, or link.",
	},
	{
		icon: "Trash2",
		label: "Delete",
		description: "Delete a comment, item, repo, or draft setting.",
	},
	{ icon: "Reply", label: "Reply", description: "Reply to a comment thread." },
	{
		icon: "Send",
		label: "Send",
		description: "Submit a reply or diff comment.",
	},
	{
		icon: "SmilePlus",
		label: "Emoji reaction",
		description: "Add or remove a comment emoji reaction.",
	},
	{
		icon: "Fish",
		label: "Murloc",
		description: "Murloc reviewer/bot comment.",
	},
	{
		icon: "ShieldCheck",
		label: "Atlantis policy",
		description: "Atlantis policy or safety comment.",
	},
	{
		icon: "ChevronDown",
		label: "Expand section",
		description: "Open dropdowns or expanded sections.",
	},
	{
		icon: "ChevronUp",
		label: "Collapse comments",
		description: "Hide expanded comments.",
	},
	{
		icon: "ChevronLeft",
		label: "Previous",
		description: "Go to the previous page or item.",
	},
	{
		icon: "ChevronRight",
		label: "Next/collapsed",
		description: "Next page or collapsed repo column.",
	},
	{
		icon: "MoreVertical",
		label: "More menu",
		description: "Open a card action menu.",
	},
	{
		icon: "RotateCcw",
		label: "Restore",
		description: "Restore, unignore, or reset an item.",
	},
	{
		icon: "EyeOff",
		label: "Hidden",
		description: "Hide, ignore, or conceal content.",
	},
	{
		icon: "GripVertical",
		label: "Drag handle",
		description: "Drag to reorder sections or columns.",
	},
	{
		icon: "FileEdit",
		label: "Review changes",
		description: "MRs waiting on review changes.",
	},
	{
		icon: "Plus",
		label: "Add",
		description: "Add a project, setting, or reviewer item.",
	},
	{ icon: "ArrowDownUp", label: "Sort", description: "Change sort order." },
	{
		icon: "ArrowUp",
		label: "Previous change",
		description: "Jump upward in the diff viewer.",
	},
	{
		icon: "ArrowDown",
		label: "Next change",
		description: "Jump downward in the diff viewer.",
	},
	{
		icon: "ChevronsUp",
		label: "Collapse all",
		description: "Collapse multiple diff sections.",
	},
	{
		icon: "ChevronsDown",
		label: "Expand all",
		description: "Expand multiple diff sections.",
	},

	{
		icon: "File",
		label: "File",
		description: "Generic file in the diff viewer.",
	},
	{
		icon: "Folder",
		label: "Folder",
		description: "Collapsed folder in the file tree.",
	},
	{
		icon: "FolderOpen",
		label: "Open folder",
		description: "Expanded folder in the file tree.",
	},
	{ icon: "FileCode", label: "Code file", description: "Source-code file." },
	{
		icon: "FileText",
		label: "Text file",
		description: "Plain text or documentation file.",
	},
	{
		icon: "FileImage",
		label: "Image file",
		description: "Image asset changed in a diff.",
	},
	{
		icon: "FileJson",
		label: "JSON/config",
		description: "JSON-like configuration file.",
	},
	{
		icon: "FileKey",
		label: "Key/secret file",
		description: "Potential key, env, or credential file.",
	},
	{
		icon: "FileLock",
		label: "Lock file",
		description: "Dependency lock file.",
	},
	{
		icon: "FileSpreadsheet",
		label: "Data file",
		description: "Spreadsheet or tabular data file.",
	},
	{
		icon: "FileTerminal",
		label: "Script",
		description: "Shell or executable script.",
	},
	{
		icon: "FileArchive",
		label: "Archive",
		description: "Compressed/archive file.",
	},
	{ icon: "FileAudio", label: "Audio file", description: "Audio asset." },
	{ icon: "FileVideo", label: "Video file", description: "Video asset." },
	{
		icon: "FileSliders",
		label: "Tunable config",
		description: "Settings or generated config file.",
	},
	{
		icon: "FileUp",
		label: "Added file",
		description: "File added in the diff.",
	},
	{
		icon: "FileDown",
		label: "Deleted file",
		description: "File removed in the diff.",
	},
	{
		icon: "Database",
		label: "Database",
		description: "Database/schema-related file.",
	},
	{
		icon: "Globe",
		label: "Web",
		description: "Web-facing file or link context.",
	},
	{
		icon: "Package",
		label: "Package",
		description: "Package or dependency metadata.",
	},
	{
		icon: "Braces",
		label: "Structured code",
		description: "Structured source or object content.",
	},
	{
		icon: "Code",
		label: "Code",
		description: "Code formatting or source context.",
	},
	{
		icon: "CodeXml",
		label: "Markup",
		description: "XML/HTML-like source file.",
	},
	{
		icon: "Bold",
		label: "Bold",
		description: "Diff comment formatting control.",
	},
	{
		icon: "Italic",
		label: "Italic",
		description: "Diff comment formatting control.",
	},
	{
		icon: "Quote",
		label: "Quote",
		description: "Insert quoted text in a comment.",
	},
	{ icon: "List", label: "List", description: "Insert a markdown list." },
	{
		icon: "WrapText",
		label: "Wrap text",
		description: "Toggle line wrapping in the diff.",
	},
	{
		icon: "Copy",
		label: "Copy",
		description: "Copy code, command, or diff content.",
	},
	{
		icon: "Save",
		label: "Save",
		description: "Save draft or diff viewer setting.",
	},
	{
		icon: "LocateFixed",
		label: "Locate",
		description: "Jump to the current file or selected line.",
	},
	{
		icon: "PanelLeftOpen",
		label: "Open sidebar",
		description: "Show the diff file sidebar.",
	},
	{
		icon: "PanelLeftClose",
		label: "Close sidebar",
		description: "Hide the diff file sidebar.",
	},
	{
		icon: "CornerDownRight",
		label: "Reply context",
		description: "Indented reply or line context.",
	},
	{
		icon: "ThumbsUp",
		label: "Positive feedback",
		description: "Approve or positive diff feedback.",
	},
	{
		icon: "ThumbsDown",
		label: "Negative feedback",
		description: "Request changes or negative diff feedback.",
	},
	{
		icon: "Palette",
		label: "Appearance",
		description: "Theme or syntax color setting.",
	},
	{
		icon: "BookOpenText",
		label: "Docs",
		description: "Open documentation or markdown help.",
	},
	{
		icon: "Edit3",
		label: "Edit",
		description: "Edit a draft comment or setting.",
	},
	{
		icon: "Loader2",
		label: "Loading",
		description: "Background request is in progress.",
	},
];

const CUSTOM_DOT_CLASS = {
	"dot-green": "bg-[var(--color-row-green)]",
	"dot-red": "bg-rose-500",
	"dot-yellow": "bg-amber-400",
	"dot-gray": "bg-slate-400",
};

const renderLegendIcon = (item) => {
	if (item.custom) {
		return (
			<span
				className={`block h-3 w-3 rounded-full ${CUSTOM_DOT_CLASS[item.custom] || "bg-slate-400"}`}
				aria-hidden="true"
			/>
		);
	}

	const Icon = ICONS[item.icon] || CircleHelp;
	return <Icon className={ICON_CLASS} aria-hidden="true" />;
};

export const IconLegendModal = ({ open, onClose }) => {
	const [query, setQuery] = useState("");
	const inputRef = useRef(null);

	useEffect(() => {
		if (!open) return;

		const handleKeyDown = (event) => {
			if (event.key === "Escape") onClose?.();
		};

		document.addEventListener("keydown", handleKeyDown);
		setTimeout(() => inputRef.current?.focus(), 0);

		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [onClose, open]);

	const filteredItems = useMemo(() => {
		const needle = query.trim().toLowerCase();
		if (!needle) return LEGEND_ITEMS;

		return LEGEND_ITEMS.filter((item) =>
			[item.label, item.description, item.icon, item.custom]
				.filter(Boolean)
				.join(" ")
				.toLowerCase()
				.includes(needle),
		);
	}, [query]);

	if (!open) return null;

	return createPortal(
		<div
			className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
			onMouseDown={onClose}
		>
			<dialog
				open
				className="flex max-h-[74vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-0 text-left shadow-2xl"
				onMouseDown={(event) => event.stopPropagation()}
				aria-labelledby="icon-legend-title"
			>
				<div className="border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]/50 px-4 py-3 text-center">
					<div className="flex items-center justify-center gap-2">
						<CircleHelp className="h-4 w-4 text-[var(--color-text-secondary)]" />
						<h2
							id="icon-legend-title"
							className="text-sm font-semibold text-[var(--color-text-primary)]"
						>
							Icon legend
						</h2>
					</div>
					<p className="mt-1 text-xs text-[var(--color-text-secondary)]">
						Meanings for dashboard, comment, and diff viewer icons.
					</p>
				</div>

				<div className="border-b border-[var(--color-border)] px-4 py-3">
					<div className="relative mx-auto max-w-sm">
						<Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
						<input
							ref={inputRef}
							type="text"
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder="Filter icons"
							className="h-8 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] pl-8 pr-8 text-center text-xs font-medium text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-border-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
							aria-label="Filter icon legend"
						/>
						{query && (
							<button
								type="button"
								onClick={() => setQuery("")}
								className="absolute inset-y-0 right-1.5 my-auto inline-flex h-5 w-5 items-center justify-center rounded-md text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-primary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
								aria-label="Clear icon filter"
							>
								<X className="h-3.5 w-3.5" />
							</button>
						)}
					</div>
				</div>

				<div className="min-h-0 overflow-y-auto p-4">
					{filteredItems.length > 0 ? (
						<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
							{filteredItems.map((item) => (
								<div
									key={`${item.icon || item.custom}-${item.label}`}
									className="flex items-start gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)]/40 px-3 py-2 text-left"
								>
									<span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[var(--color-bg-primary)]">
										{renderLegendIcon(item)}
									</span>
									<span className="min-w-0">
										<span className="block text-xs font-semibold text-[var(--color-text-primary)]">
											{item.label}
										</span>
										<span className="mt-0.5 block text-[11px] leading-snug text-[var(--color-text-secondary)]">
											{item.description}
										</span>
									</span>
								</div>
							))}
						</div>
					) : (
						<div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 py-6 text-center text-sm text-[var(--color-text-secondary)]">
							No icons match “{query}”.
						</div>
					)}
				</div>

				<div className="flex justify-center border-t border-[var(--color-border)] bg-[var(--color-bg-secondary)]/50 px-4 py-3">
					<button
						type="button"
						onClick={onClose}
						className="inline-flex h-8 items-center justify-center rounded-md border border-[var(--color-border)] px-3 text-xs font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
					>
						Close
					</button>
				</div>
			</dialog>
		</div>,
		document.body,
	);
};

export default IconLegendModal;
